import {
  DictionaryItem as PrismaDictionaryItem,
  DictionaryItemQaCount,
  DictionaryItemImage as PrismaDictionaryItemImage,
} from "@prisma/client";
import { Translate } from "next-translate";
import * as zod from "zod";
import {
  DICTIONARY_ITEM_IMAGE_MIN_HEIGHT,
  DICTIONARY_ITEM_IMAGE_MIN_WIDTH,
} from "~/lib/constants";

export type DictionaryItem = PrismaDictionaryItem & {
  images: PrismaDictionaryItemImage[];
  qaCountWithLocales: DictionaryItemQaCount[];
};

export type DictionaryItemImage = PrismaDictionaryItemImage & {
  dictionaryItem: DictionaryItemWithParent;
};

interface DictionaryItemWithRegexp extends DictionaryItem {
  regexp: string;
}

export type DictionaryItemWithChildren = DictionaryItem & {
  children: DictionaryItemWithChildren[];
  parent: null;
};

export type DictionaryItemWithFamily = DictionaryItem & {
  children: DictionaryItemWithFamily[];
  parent: DictionaryItemWithFamily | null;
};
export type DictionaryItemWithParent = DictionaryItem & {
  parent?: DictionaryItemWithParent | null;
  questionAnswers?: { questionAnswerId: string }[];
};

interface DictionaryItemIDictionary {
  [index: string]: DictionaryItem;
}

export const DICTIONARY_ITEM_SCHEMA = (t: Translate) =>
  zod.object({
    name: zod.string().nonempty({
      message: t("form:validation.empty-input", {
        name: "名称",
      }),
    }),
    regexp: zod.string().nullable(),
    slug: zod
      .string()
      .nonempty({
        message: t("form:validation.empty-input", {
          name: "URLで使うSlug",
        }),
      })
      .refine(validateDictinaryItemSlug, {
        message: t("form:validation.invalid-format"),
      }),
  });

export const DICTIONARY_ITEM_IMAGE_WIDTH_TYPE_STORAGE_KEY =
  "_dictionary_item_width_type_";

export const DICTIONARY_ITEM_INSERT_OPTION_STORAGE_KEY =
  "_dictionary_item_insert_option_";

export const DICTIONARY_ITEM_IMAGE_WIDTHS = [
  "original",
  "256",
  "192",
  "128",
  "64",
  "48",
  "32",
  "24",
] as const;

export const DICTIONARY_ITEM_INSERT_OPTIONS = [
  "image",
  "image_and_caption",
] as const;

export type DictionaryItemWidthWidthOptions =
  typeof DICTIONARY_ITEM_IMAGE_WIDTHS;

export type DictionaryItemImageWidthType = Readonly<
  DictionaryItemWidthWidthOptions[number]
>;

export type DictionaryItemImageMap = {
  [key in DictionaryItemImageWidthType]: string;
};

export type DictionaryItemInsertOptions = typeof DICTIONARY_ITEM_INSERT_OPTIONS;

export type DictionaryItemInsertOptionType = Readonly<
  DictionaryItemInsertOptions[number]
>;

export enum DictionaryItemWriteError {
  invalidSlug = "invalidSlug",
  nameAlreadyInUse = "nameAlreadyInUse",
  slugAlreadyInUse = "slugAlreadyInUse",
}

export function isDictionaryItemWriteError(
  errorMessage?: string
): errorMessage is DictionaryItemWriteError {
  if (!errorMessage) {
    return false;
  }
  return [
    DictionaryItemWriteError.invalidSlug,
    DictionaryItemWriteError.nameAlreadyInUse,
    DictionaryItemWriteError.slugAlreadyInUse,
  ].includes(errorMessage as DictionaryItemWriteError);
}

export function getDictionaryItemImageWidthTypeDisplayValue(
  widthType: DictionaryItemImageWidthType
) {
  if (widthType === "original") {
    return "オリジナル";
  }

  return `${widthType}px`;
}

export function getDictionaryItemInsertOptionTypeDisplayValue(
  insertOptionType: DictionaryItemInsertOptionType
) {
  switch (insertOptionType) {
    case "image":
      return "画像のみ";
    case "image_and_caption":
      return "画像+キャプション";
    default:
      return "";
  }
}

export function isValidDictionaryItemImageWidthType(
  type: any
): type is DictionaryItemImageWidthType {
  return DICTIONARY_ITEM_IMAGE_WIDTHS.includes(type);
}

export function isValidDictionaryItemInsertOptionType(
  type: any
): type is DictionaryItemInsertOptionType {
  return DICTIONARY_ITEM_INSERT_OPTIONS.includes(type);
}

export function getDictionaryItemImageUrlForWidth({
  url,
  width,
}: {
  url: DictionaryItemImage["url"] | null;
  width: DictionaryItemImageWidthType;
}) {
  if (!url) {
    return;
  }
  if (!isValidDictionaryItemImageWidthType(width)) {
    return url;
  }

  return url.replace("original", width);
}

export function validateDictinaryItemSlug(slug: string): boolean {
  if (!slug) {
    return false;
  }

  return !!slug.match(/^[0-9a-z_-]+$/);
}

export function generateDictionaryItemDisplayName({
  dictionaryItem,
  seperator = "/",
}: {
  dictionaryItem: DictionaryItemWithParent;
  seperator?: string;
}) {
  const names: string[] = [];

  let currentDictionary: DictionaryItemWithParent["parent"] = dictionaryItem;

  while (currentDictionary) {
    names.unshift(currentDictionary.name);
    currentDictionary = currentDictionary.parent;
  }

  return names.join(seperator);
}

export function generateDictionaryItemFamilies({
  dictionaryItems = [],
  disableInitialFilter = false,
  parentId = null,
}: {
  dictionaryItems: DictionaryItemWithParent[];
  disableInitialFilter?: boolean;
  parentId?: DictionaryItemWithFamily["id"] | null;
}): DictionaryItemWithFamily[] {
  if (disableInitialFilter) {
    return dictionaryItems.map<DictionaryItemWithFamily>(
      (item) =>
        ({
          ...item,
          children: generateDictionaryItemFamilies({
            dictionaryItems: dictionaryItems,
            parentId: item.id,
          }),
        } as DictionaryItemWithFamily)
    );
  }
  return dictionaryItems
    .filter((item) => item.parentId === parentId)
    .map<DictionaryItemWithFamily>(
      (item) =>
        ({
          ...item,
          children: generateDictionaryItemFamilies({
            dictionaryItems: dictionaryItems,
            parentId: item.id,
          }),
        } as DictionaryItemWithFamily)
    );
}

export function getDictionaryItemQaCountForLocale({
  locale,
  dictionaryItem,
}: {
  locale: DictionaryItemQaCount["locale"] | undefined;
  dictionaryItem: DictionaryItem;
}) {
  const qaCountWithLocale = dictionaryItem.qaCountWithLocales.find(
    (qaCountWithLocale) => qaCountWithLocale.locale === locale
  );

  if (!qaCountWithLocale) {
    return 0;
  }

  return qaCountWithLocale.qaCount;
}

function isDictionaryItemWithRegexp(
  dictionaryItem: DictionaryItem
): dictionaryItem is DictionaryItemWithRegexp {
  return !!dictionaryItem.regexp;
}

export function filterDictionaryItemsWithRegexp(
  dictionaryItems: DictionaryItem[] = []
): DictionaryItemWithRegexp[] {
  return dictionaryItems.filter(isDictionaryItemWithRegexp);
}

export function pickLatestDictionaryItemImageUrl(
  dictionaryItem: DictionaryItem
) {
  if (!dictionaryItem?.images?.length) {
    return null;
  }

  const latestDictionaryItemImage = dictionaryItem.images.reduce(
    (latestImage, image) => {
      if (image.updatedAt > latestImage.updatedAt) {
        return image;
      }

      return latestImage;
    },
    dictionaryItem.images[0]
  );

  return latestDictionaryItemImage.url;
}

interface ValidDictionaryItemImageSize {
  width: number;
  height: number;
}

export function isValidDictionaryItemImageSize(props: {
  width?: number | null;
  height?: number | null;
}): props is ValidDictionaryItemImageSize {
  return (
    typeof props.width === "number" &&
    typeof props.height === "number" &&
    props.width >= DICTIONARY_ITEM_IMAGE_MIN_WIDTH &&
    props.height >= DICTIONARY_ITEM_IMAGE_MIN_HEIGHT
  );
}

export function getDictionaryItemAncestors({
  dictionaryItem,
  includeSelf = true,
}: {
  dictionaryItem: DictionaryItemWithParent;
  includeSelf?: boolean;
}) {
  const ancestors: DictionaryItemWithParent[] = [];

  let currentParent: DictionaryItemWithParent | null | undefined = includeSelf
    ? dictionaryItem
    : dictionaryItem.parent;

  while (currentParent) {
    ancestors.unshift(currentParent);
    currentParent = currentParent.parent;
  }

  return ancestors;
}

export function dictionaryItemQaCountSort(
  a: DictionaryItem,
  b: DictionaryItem
): number {
  return toDictionaryItemTotalQaCount(b) - toDictionaryItemTotalQaCount(a);
}

function toDictionaryItemTotalQaCount(
  dictionaryItem: DictionaryItem | undefined
): number {
  if (
    !dictionaryItem?.qaCountWithLocales ||
    !dictionaryItem.qaCountWithLocales.length
  ) {
    return 0;
  }
  const qaCounts = dictionaryItem.qaCountWithLocales.map((q) => q.qaCount);
  return qaCounts.reduce((prev, next) => prev + next) || 0;
}

export function toDictionaryItemIDictionary(
  dictionaryItems: DictionaryItem[] | undefined
): DictionaryItemIDictionary {
  if (!dictionaryItems?.length) {
    return {};
  }

  return dictionaryItems?.reduce(
    (arr: DictionaryItemIDictionary, d) => ((arr[d.id] = d), arr),
    {}
  );
}
